import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase/firebase";
import { useAuth } from "../contexts/AuthContext";

import toast from "../utils/toast";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "./login.css";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { login, user } = useAuth();

  // Redirect already logged-in admin
  useEffect(() => {
    if (user) {
      navigate("/admin", { replace: true });
    }
  }, [user, navigate]);

  // Validation
  const validate = () => {
    if (!username.trim()) {
      toast.error("Username is required");
      return false;
    }
    if (!password.trim()) {
      toast.error("Password is required");
      return false;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return false;
    }
    return true;
  };

  // Handle Login
  const handleLogin = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);

    try {
      const adminRef = collection(db, "admin");
      const q = query(adminRef, where("username", "==", username.trim()));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const data = querySnapshot.docs[0].data();

        if (data.password === password) {
          login({
            username: data.username,
            role: "superadmin",
            name: data.username,
          });

          toast.success("Login successful", {
            position: "top-right",
            autoClose: 2000,
          });

          setTimeout(() => navigate("/admin"), 1200);
          return;
        }
      }

      // Invalid credentials
      toast.error("Invalid username or password.", {
        position: "top-right",
        autoClose: 3000,
      });

      setLoading(false);
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Login failed. Please try again.", {
        position: "top-right",
        autoClose: 3000,
      });

      setLoading(false);
    }
  };

  return (
    <>
      <Header />

      <div className="login-page">
        {/* Decorative background blobs */}
        <span className="login-blob login-blob--1" aria-hidden="true" />
        <span className="login-blob login-blob--2" aria-hidden="true" />

        <div className="login-card">
          {/* Brand mark */}
          <div className="login-brand">
            <span className="login-brand-dot" />
            <span className="login-brand-name">Admin Panel</span>
          </div>

          <h1 className="login-title">Welcome back</h1>
          <p className="login-subtitle">
            Sign in to continue to your dashboard
          </p>

          <form onSubmit={handleLogin} className="login-form" noValidate>
            <div className="input-group">
              <label htmlFor="login-username">Username</label>
              <div className="input-wrap">
                <span className="input-icon">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </span>
                <input
                  id="login-username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                  autoComplete="username"
                />
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="login-password">Password</label>
              <div className="input-wrap">
                <span className="input-icon">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </span>
                <input
                  id="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  autoComplete="current-password"
                />
              </div>
            </div>

            <button
              type="submit"
              className={`login-page-btn${loading ? " loading" : ""}`}
              disabled={loading}
            >
              {loading ? (
                <span className="btn-spinner" aria-label="Signing in…" />
              ) : (
                "Sign In"
              )}
            </button>
          </form>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Login;